import styled from 'styled-components';
import { LeftTooltip } from 'ui/tooltip';
import { ButtonGroup } from 'ui/buttonGroup';
import { Radio } from 'ui/radio';
import {
  body2,
  body2Bold,
  caption,
  colors,
  spacing,
  subtitle2,
  fontWeights,
  Subtitle2,
  linkColors,
  link2,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

/** Form - interactive elements */

export const RadioButton = styled(Radio)`
  flex-shrink: 0;
`;

export const RadioLabel = styled.label<{ isDisabled?: boolean }>`
  ${body2};
  flex-wrap: wrap;
  margin-bottom: 0;
  top: -${spacing.single};
  position: relative;

  color: ${({ isDisabled }) => (isDisabled ? colors.textDisabled : colors.textPrimary)};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
`;

export const RadioOption = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${spacing.half};
`;

export const PaddedRadioOption = styled(RadioOption)`
  padding-bottom: ${spacing.half};
`;

export const StyledButtonGroup = styled(ButtonGroup)`
  margin-right: ${spacing.half};
`;

/** Form  - text elements */

export const FormBodyText = styled.p`
  ${body2};
  margin-bottom: 0; /** default margin causes text to be off-center */
`;

export const FormHeading = styled(Subtitle2)`
  display: flex;
  align-items: center;
  gap: ${spacing.eighth};
`;

export const FormHeadingText = styled.p`
  ${subtitle2};
  margin-bottom: 0; /** default margin causes text to be off-center */
`;

export const FormDescriptionText = styled.p`
  ${caption};
  color: ${colors.textSecondary};
`;

export const StyledLink = styled.a`
  ${link2};
`;

export const StyledLinkWithIcon = styled(StyledLink)`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.quarter};
  width: max-content;

  path {
    stroke: ${colors.textLink};
  }
  /** changes icon color on hover as well */
  :hover {
    color: ${linkColors.linkHover};
    path {
      stroke: ${linkColors.linkHover};
    }
  }
`;

export const WrappingStyledLinkWithIcon = styled(StyledLinkWithIcon)`
  display: inline-block;
  width: auto;

  svg {
    position: relative;
    top: ${spacing.eighth};
    left: ${spacing.quarter};
  }
`;

export const StyledSubtitle = styled.div`
  ${subtitle2};
  line-height: 1;
  margin-left: ${spacing.quarter};
`;

export const ToggleLabel = styled.label`
  ${subtitle2};
  margin-bottom: 0; /** default margin causes text to be off-center */
`;

/** Input wrappers */

export const InputWrapper = styled.div`
  width: ${pxToRem(110)};
  input {
    width: ${spacing.base};
  }
`;

export const SmallInputWrapper = styled(InputWrapper)`
  width: ${pxToRem(90)};
`;

export const LargeInputWrapper = styled(InputWrapper)`
  width: ${pxToRem(165)};
`;

/** Various other wrappers */

export const ButtonGroupWrapper = styled.div`
  display: flex;
  align-items: center;
`;
export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
`;

// adjusts margin for inputs that can be toggled to
// expand open additional form elements below
export const ExpandableFormSectionContent = styled.div<{ isExpanded: boolean }>`
  margin-bottom: ${({ isExpanded }) => (isExpanded ? spacing.threeQuarter : spacing.plusHalf)};
`;

// adjusts margin for inputs that can be toggled to
// expand open additional form elements below
export const PaddedExpandableFormSectionContent = styled.div<{ isExpanded: boolean }>`
  margin-bottom: ${({ isExpanded }) => (isExpanded ? spacing.base : spacing.double)};
`;

export const FormHeadingWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: ${spacing.half};

  > * + * {
    margin-left: ${spacing.half};
  }
`;

export const FormSectionContent = styled.div`
  > * + * {
    margin-top: ${spacing.threeQuarter};
  }
  margin-bottom: ${spacing.plusHalf};
`;

export const IndentedFormSectionContent = styled(FormSectionContent)`
  margin-left: ${spacing.base};
`;

export const PlusHalfIndentedFormSectionContent = styled(FormSectionContent)`
  margin-left: ${spacing.plusHalf};
`;

export const PaddedFormSectionContent = styled.div`
  margin-bottom: ${spacing.double};
`;

export const HalfPaddedFormSectionContent = styled.div`
  margin-bottom: ${spacing.half};
`;

export const BasePaddedFormSectionContent = styled.div`
  margin-bottom: ${spacing.base};
`;

export const IndentedPaddedFormSectionContent = styled(PaddedFormSectionContent)`
  margin-left: ${spacing.plusHalf};
`;

export const FlexColumnHalfGapFormSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const RowWrapper = styled.div`
  align-items: center;
  display: flex;

  > * + * {
    margin-left: ${spacing.half};
  }
`;

export const TallRowWrapper = styled(RowWrapper)`
  :not(:last-child) {
    margin-bottom: ${spacing.threeQuarter};
  }
`;

export const IndentedRowWrapper = styled(RowWrapper)`
  margin-left: ${spacing.plusHalf};
`;

export const SelectHelper = styled.div`
  margin-left: ${spacing.plusHalf};
`;

export const SelectLink = styled.a`
  color: ${colors.brandPurple};
  text-decoration: underline;
`;

export const BoldText = styled.span`
  font-weight: ${fontWeights.bold};
`;

export const CheckboxLabel = styled.div<{ htmlFor: string }>`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  ${body2Bold};
`;

export const StyledCaption = styled.div`
  ${caption};
  color: ${colors.textSecondary};
  margin-left: ${spacing.plusHalf};
  margin-top: ${spacing.threeEights};
`;

export const CodeBlock = styled.code`
  color: ${colors.red100};
  background-color: ${colors.red10};
`;

export const StyledLeftTooltip = styled(LeftTooltip)`
  width: 100% !important;
`;
